import React from "react";
import { Chip, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { formatFullName } from "utils/formatters/NameFormatters";
import type { ContactDto } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { EmptyValue } from "components/MutedPlaceholder";
import { Overflow, OverflowMenu } from "components/OverflowMenu/OverflowMenu";
import { RbacPolicies } from "components/authorization/RbacPolicies";

export interface ContactsListProps {
  contacts: ContactDto[];
  onEdit: (contact: ContactDto) => void;
  onRemove: (contact: ContactDto) => void;
}

function NoContactsEmptyState() {
  return (
    <Stack spacing={1} sx={{ alignItems: "center", py: 6 }}>
      <Typography variant="subtitle1">No contacts on this account yet.</Typography>
      <Typography variant="body2" color="text.secondary">
        Add a billing contact before the next order or renewal — invoices need somewhere to go.
      </Typography>
    </Stack>
  );
}

function buildColumns(onEdit: ContactsListProps["onEdit"], onRemove: ContactsListProps["onRemove"]) {
  const columns: GridColDef<ContactDto>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) => formatFullName(row),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 160,
      sortable: false,
      renderCell: (params) => params.row.phone ?? <EmptyValue />,
    },
    {
      field: "roles",
      headerName: "Roles",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ height: "100%", alignItems: "center" }}>
          {[...params.row.roles].sort().map((role) => (
            <Chip key={role} label={role} size="small" variant="outlined" />
          ))}
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      align: "right",
      renderCell: (params) => (
        <OverflowMenu
          items={[
            Overflow.policyAuthorizedMenuItem(RbacPolicies.BillingAccounts.ReadWrite, "Edit", () => onEdit(params.row)),
            Overflow.policyAuthorizedMenuItem(RbacPolicies.BillingAccounts.ReadWrite, "Remove", () =>
              onRemove(params.row)
            ),
          ]}
        />
      ),
    },
  ];
  return columns;
}

export function ContactsList({ contacts, onEdit, onRemove }: ContactsListProps) {
  if (contacts.length === 0) {
    return <NoContactsEmptyState />;
  }

  return (
    <DataTable
      rows={contacts}
      columns={buildColumns(onEdit, onRemove)}
      pagingMode="client"
      sortingMode="client"
      pageSize={10}
      pageSizeOptions={[10, 20]}
    />
  );
}
